<script lang="ts" setup>
  import PlaidLogo from '@leaflink/stash/assets/logos/plaid-logo-full.svg';
  import Button from '@leaflink/stash/Button.vue';
  import Card from '@leaflink/stash/Card.vue';
  import CardContent from '@leaflink/stash/CardContent.vue';
  import CardHeader from '@leaflink/stash/CardHeader.vue';
  import Chip from '@leaflink/stash/Chip.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import PlaidLink from '@leaflink/stash/PlaidLink.vue';
  import useDialog from '@leaflink/stash/useDialog';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { SPLIT_NAMES } from '@/constants';
  import { env } from '@/constants/env';
  import { BillingPaymentMethod } from '@/models/billing';
  import { useSettingsStore } from '@/piniaStores/settings/settings.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { postLinkToken, postVerifyAccount } from '@/services/api/plaid';
  import { isHttpError, notifyError } from '@/utils/errors';
  import { getBillingPaymentMethodInfo } from '@/utils/getBillingPaymentMethodInfo';

  import PaymentMethodStatusLabel from './PaymentMethodStatusLabel.vue';

  export interface PaymentMethodListCardProps {
    paymentMethod: BillingPaymentMethod;
    showTrashIcon?: boolean;
  }

  const dialog = useDialog();

  const { deleteCompanyPaymentMethod, setCompanyPaymentMethodToDefault } = useSettingsStore();
  const { isLoadingBillingPaymentMethods } = storeToRefs(useSettingsStore());
  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const props = withDefaults(defineProps<PaymentMethodListCardProps>(), {
    showTrashIcon: true,
  });

  const isPlansAndBillingEnabled = computed(() => getIsSplitEnabled.value(SPLIT_NAMES.PLANS_AND_BILLING));

  const showPlaidLinkFlow = computed(() => {
    return (
      props.paymentMethod.contentObject.status === 'pending_manual_verification' ||
      props.paymentMethod.contentObject.status === 'login_required'
    );
  });

  const isLinkedBankAccount = computed(
    () => props.paymentMethod.contentObject.status === 'linked' && props.paymentMethod.contentName === 'bankaccount',
  );

  const paymentMethodInfo = computed(() => getBillingPaymentMethodInfo({ method: props.paymentMethod }));

  const emit = defineEmits<{
    (e: 'update:set-default'): Promise<void>;
    (e: 'delete:payment-method'): Promise<void>;
    (e: 'update:verify-account'): Promise<void>;
    (e: 'click:plaid-link'): Promise<void>;
  }>();

  async function setDefault() {
    await setCompanyPaymentMethodToDefault(props.paymentMethod.id);
    emit('update:set-default');
  }

  async function removePaymentMethod() {
    if (isPlansAndBillingEnabled.value) {
      const confirmation = await dialog.open({
        dangerZone: true,
        header: i18n.t('settings.plansAndBilling.dialogs.deletePaymentMethod.header'),
        description: i18n.t('settings.plansAndBilling.dialogs.deletePaymentMethod.description'),
        confirmText: i18n.t('settings.plansAndBilling.dialogs.deletePaymentMethod.confirmText'),
      });

      if (confirmation?.isCanceled) return;
    }

    await deleteCompanyPaymentMethod(props.paymentMethod.id);

    emit('delete:payment-method');
  }

  function getToken(paymentMethodId: string) {
    if (paymentMethodId) {
      return postLinkToken({ account_id: paymentMethodId });
    }
    return postLinkToken();
  }

  async function verifyAccount(paymentMethod: BillingPaymentMethod, successVerification = true) {
    try {
      await postVerifyAccount({
        account_id: paymentMethod.contentObject.id,
        verification_successful: successVerification,
      });
      emit('update:verify-account');
    } catch (err) {
      if (isHttpError(err)) {
        notifyError(err, 'Unable to verify your account.');
      } else {
        throw err;
      }
    }
  }

  async function exitVerifyAccount(paymentMethod: BillingPaymentMethod, error: { error_code: string }) {
    if (error?.error_code === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
      await verifyAccount(paymentMethod, false);
    }
  }
</script>

<template>
  <Card class="tw-grid tw-content-start tw-gap-6 tw-p-6 [&>*]:tw-m-0 [&>*]:tw-p-0" data-test="card|payment-method-list">
    <template #top-left>
      <Chip v-if="props.paymentMethod.isDefault" color-scheme="teal" shade="light" data-test="card|stash-chip">
        {{ i18n.t('settings.billing.editPaymentMethodsModal.paymentMethodInformation.default') }}
      </Chip>

      <Button
        v-else
        inline
        :disabled="props.paymentMethod.contentObject.status !== 'linked' || isLoadingBillingPaymentMethods"
        @click="setDefault()"
      >
        {{ $t('settings.billing.editPaymentMethodsModal.paymentMethodInformation.setDefault') }}
      </Button>
    </template>

    <template v-if="props.showTrashIcon" #top-right>
      <Button
        class="-tw-mt-3 -tw-mr-3 -tw-mb-3"
        icon
        data-test="button|trashcan"
        :title="i18n.t('common.delete')"
        @click="removePaymentMethod()"
      >
        <Icon name="trashcan" />
      </Button>
    </template>

    <CardHeader
      class="!tw-px-0"
      :title="paymentMethodInfo.extendedName"
      :text="paymentMethodInfo.truncatedNumber"
      data-test="stash-card-header"
    />

    <CardContent data-test="stash-card-content" class="!tw-px-0">
      <div class="tw-flex tw-gap-2 tw-items-center">
        <PaymentMethodStatusLabel
          :status="props.paymentMethod.contentObject.status"
          data-test="card|payment-status-label"
        />

        <template v-if="isLinkedBankAccount">
          <PlaidLogo height="20px" data-test="plaid-logo" />
        </template>

        <template v-else-if="showPlaidLinkFlow">
          <PlaidLink
            v-slot="{ open }"
            :create-token="() => getToken(props.paymentMethod.contentObject.id)"
            :env="env.VITE_PLAID_ENV"
            :storage-key="`${env.MODE}-plaid`"
            @success="() => verifyAccount(props.paymentMethod)"
            @exit="(error) => exitVerifyAccount(props.paymentMethod, error)"
            @error="(err) => notifyError(err)"
            @click="emit('click:plaid-link')"
          >
            <Button inline :disabled="open" data-test="verify-account">
              {{
                props.paymentMethod.contentObject.status === 'login_required'
                  ? i18n.t('settings.billing.editPaymentMethodsModal.paymentMethodInformation.relinkPlaid')
                  : i18n.t('settings.billing.editPaymentMethodsModal.paymentMethodInformation.verifyPlaid')
              }}
            </Button>
          </PlaidLink>
        </template>
      </div>
    </CardContent>
  </Card>
</template>
